import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { getHeaders } from "../../../helpers/helper";
import { OrderListImportResult, OrderListMasterDataResponse } from "../../../models/orderListModels";
import axiosInstance from "../../../shared/config/axiosInstance";
import { MutationKeys } from "../../../shared/react-query/mutationKeys";

export type OrderListDataQueryKey =
  | "ORDER_LIST_INGREDIENTS"
  | "ORDER_LIST_RECIPES"
  | "ORDER_LIST_INGREDIENT_RECIPES";

export interface OrderListDataEndpoint {
  queryKey: OrderListDataQueryKey;
  getPath: string;
  setPath: string;
}

export interface OrderListImportErrorBody {
  errors?: string[];
}

export type OrderListImportError = AxiosError<OrderListImportResult | OrderListImportErrorBody>;

export const useOrderListMasterData = <T>(endpoint: OrderListDataEndpoint) => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery<OrderListMasterDataResponse<T>>({
    queryKey: [endpoint.queryKey],
    queryFn: async () => {
      const headers = await getHeaders(getAccessTokenSilently);
      const { data } = await axiosInstance.get<OrderListMasterDataResponse<T>>(endpoint.getPath, { headers });
      return data;
    },
    enabled: !!getAccessTokenSilently,
    staleTime: 0,
    refetchOnMount: true,
  });
};

export const useUploadOrderListMasterData = (endpoint: OrderListDataEndpoint) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation<OrderListImportResult, OrderListImportError, File>({
    mutationKey: [MutationKeys.ORDER_LIST_MASTER_DATA, endpoint.queryKey],
    mutationFn: async (file) => {
      const authHeaders = await getHeaders(getAccessTokenSilently);
      const form = new FormData();
      form.append("file", file, file.name);
      const { data } = await axiosInstance.post<OrderListImportResult>(endpoint.setPath, form, {
        headers: {
          ...authHeaders,
          "Content-Type": "multipart/form-data",
        },
      });
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [endpoint.queryKey] });
    },
  });
};